/**
 * GPX 1.1 parser.
 *
 * Extracts track points from `<trkpt>` elements (falling back to `<rtept>`
 * and then `<wpt>` if the file has no actual track) and decodes the common
 * Garmin / FlyQ extensions for speed/course.
 *
 * Requires a DOMParser \u2014 in Vitest tests use the jsdom environment.
 */
import type { TrackPoint } from './track-types';

const M_TO_FT = 3.28084;
const MPS_TO_KTS = 1.94384;

function parseTimeAttr(el: Element): number | undefined {
  const t = el.getElementsByTagName('time')[0]?.textContent?.trim();
  if (!t) return undefined;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : undefined;
}

function parseExt(el: Element, localName: string): string | undefined {
  // Walk children regardless of namespace prefix
  for (const child of Array.from(el.getElementsByTagName('*'))) {
    if (child.localName === localName && child.textContent) {
      return child.textContent.trim();
    }
  }
  return undefined;
}

function pointFromGpxElement(el: Element): TrackPoint | null {
  const lat = parseFloat(el.getAttribute('lat') ?? '');
  const lon = parseFloat(el.getAttribute('lon') ?? '');
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const eleText = el.getElementsByTagName('ele')[0]?.textContent?.trim();
  const altitudeM = eleText ? parseFloat(eleText) : undefined;

  // Garmin TrackPointExtension: gpxtpx:speed (m/s), gpxtpx:course (deg)
  const speedText = parseExt(el, 'speed');
  const courseText = parseExt(el, 'course');
  const speedMs = speedText ? parseFloat(speedText) : undefined;
  const courseDeg = courseText ? parseFloat(courseText) : undefined;

  return {
    timestamp: parseTimeAttr(el),
    latitude: lat,
    longitude: lon,
    altitudeFt: Number.isFinite(altitudeM) ? (altitudeM as number) * M_TO_FT : undefined,
    groundSpeedKts: Number.isFinite(speedMs) ? (speedMs as number) * MPS_TO_KTS : undefined,
    heading: Number.isFinite(courseDeg) ? courseDeg : undefined,
  };
}

export function parseGpx(content: string): { name: string | undefined; points: TrackPoint[] } {
  const doc = new DOMParser().parseFromString(content, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('GPX parse error: invalid XML');
  }

  const trkpts = Array.from(doc.getElementsByTagName('trkpt'));
  const source = trkpts.length > 0
    ? trkpts
    : Array.from(doc.getElementsByTagName('rtept'));
  const finalSource = source.length > 0
    ? source
    : Array.from(doc.getElementsByTagName('wpt'));

  const points: TrackPoint[] = [];
  for (const el of finalSource) {
    const p = pointFromGpxElement(el);
    if (p) points.push(p);
  }

  const name = doc.getElementsByTagName('trk')[0]?.getElementsByTagName('name')[0]?.textContent
    ?? doc.getElementsByTagName('metadata')[0]?.getElementsByTagName('name')[0]?.textContent
    ?? undefined;

  return { name: name?.trim() || undefined, points };
}
